import { ArrowBack, Print } from "@mui/icons-material";
import { Button, Tab, Tabs, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EpiDonut from "../../components/epi/EpiDonut";
import EpiEmployeeHeader from "../../components/epi/EpiEmployeeHeader";
import { EpiStatusChip } from "../../components/epi/EpiStatusChip";
import {
	EPI_COLORS, actionStatusColors, actionStatusLabels, reasonLabel, resultLabels
} from "../../components/epi/epiColors";
import { EpiComplianceService } from "../../services/EpiComplianceService";
import { EpiDossierDTO } from "../../types/EpiDossierDTO";
import { EpiPdfService, blobToDataUrl, loadCompanyLogo } from "../../utils/EpiPdfService";
import { useUtils } from "../../utils/utilFunctions";
import { IMMEDIATE_ACTIONS } from "../epi-inspection-wizard/steps/EpiChecklistStep";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiInspectionDossier.scss';

const itemStatusLabels: Record<string, string> = {
	OK: 'Conforme',
	NAO_OK: 'Não conforme',
	NAO_APLICAVEL: 'Não aplicável'
};

const itemStatusColors: Record<string, string> = {
	OK: EPI_COLORS.success,
	NAO_OK: EPI_COLORS.danger,
	NAO_APLICAVEL: EPI_COLORS.neutral
};

const EpiInspectionDossier: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [dossier, setDossier] = useState<EpiDossierDTO>();
	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(true);
	const [files, setFiles] = useState<Record<string, string>>({});
	const { inspectionId } = useParams();
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const { showError } = useAlert();
	const service = new EpiComplianceService();

	useEffect(() => {
		const load = async () => {
			const response = await service.getDossier(Number(inspectionId));
			setDossier(response?.data);
			setLoading(false);
		};
		load();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_INSPECTION');
		}
	}, []);

	useEffect(() => {
		const loadFiles = async () => {
			if (!dossier) {
				return;
			}
			const names = dossier.evidenceFiles
				.concat(dossier.signatures.map(signature => signature.fileName))
				.filter(name => !!name);
			for (const name of names) {
				const response = await service.getFile(Number(inspectionId), name);
				if (response?.data) {
					const url = URL.createObjectURL(response.data as Blob);
					setFiles(previous => ({ ...previous, [name]: url }));
				}
			}
		};
		loadFiles();
	}, [dossier]);

	async function printDossier() {
		if (!dossier) {
			return;
		}
		try {
			const images: Record<string, string> = {};
			for (const name of dossier.evidenceFiles) {
				const response = await service.getFile(Number(inspectionId), name);
				if (response?.data) {
					images[name] = await blobToDataUrl(response.data as Blob);
				}
			}
			for (const signature of dossier.signatures) {
				const response = await service.getFile(Number(inspectionId), signature.fileName);
				if (response?.data) {
					images[signature.fileName] = await blobToDataUrl(response.data as Blob);
				}
			}
			new EpiPdfService().exportDossierToPdf(dossier, images, await loadCompanyLogo(dossier.inspection.companyId));
		} catch {
			showError('Não foi possível gerar o relatório.');
		}
	}

	if (loading) {
		return (
			<Fragment>
				<AppBar
					modulo="Inspeção de EPIs"
				/>
				{getLoading()}
			</Fragment>
		);
	}

	if (!dossier) {
		return (
			<Fragment>
				<AppBar
					modulo="Inspeção de EPIs"
				/>
				<div className="epi-dossier">
					<Typography variant="h6">Inspeção não encontrada.</Typography>
					<Button variant="contained" onClick={() => navigate('/inspecao-epis/inspecoes')}>
						Voltar para inspeções
					</Button>
				</div>
			</Fragment>
		);
	}

	const inspection = dossier.inspection;

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-dossier">
				<div className="topo">
					<Button
						variant="outlined"
						startIcon={<ArrowBack />}
						onClick={() => navigate('/inspecao-epis/inspecoes')}
					>
						Voltar para inspeções
					</Button>
					{permissions.includes('EXPORT_EPI_REPORT') && (
						<Button variant="contained" startIcon={<Print />} onClick={printDossier}>
							Imprimir relatório
						</Button>
					)}
					<div className="identificacao-inspecao">
						<Typography variant="h6">Inspeção nº {inspection.number || '—'}</Typography>
						<EpiStatusChip
							label={inspection.status === 'CANCELADA' ? 'Cancelada' : 'Concluída'}
							color={inspection.status === 'CANCELADA'
								? EPI_COLORS.neutral
								: EPI_COLORS.success}
							filled
						/>
					</div>
					<div className="resultado-geral">
						<span>Resultado geral</span>
						<EpiStatusChip
							label={resultLabels[inspection.result] || '—'}
							color={inspection.result === 'NAO_CONFORME'
								? EPI_COLORS.danger
								: EPI_COLORS.success}
						/>
					</div>
				</div>
				<EpiEmployeeHeader
					name={inspection.employeeName}
					registration={inspection.employeeRegistration}
					position={inspection.positionName}
					sector={inspection.sectorName}
					inspectionDate={inspection.inspectionDate}
					inspectionNumber={inspection.number}
				/>
				<Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
					<Tab label="Dados gerais" />
					<Tab label="Itens inspecionados" />
					<Tab label={`Não conformidades (${dossier.nonConformItems})`} />
					<Tab label={`Ações corretivas (${dossier.correctiveActions.length})`} />
					<Tab label="Fotos e evidências" />
					<Tab label="Histórico" />
				</Tabs>
				<div className="conteudo">
					{tab === 0 && (
						<div className="dados-gerais">
							<div className="painel">
								<div className="campos">
									<span><strong>Empresa: </strong>{inspection.companyName || '—'}</span>
									<span><strong>Setor: </strong>{inspection.sectorName || '—'}</span>
									<span><strong>Cargo: </strong>{inspection.positionName || '—'}</span>
									<span><strong>Colaborador: </strong>{inspection.employeeName || '—'}</span>
									<span><strong>Matrícula: </strong>{inspection.employeeRegistration || '—'}</span>
									<span><strong>Responsável: </strong>{inspection.responsibleName || '—'}</span>
									<span><strong>Próxima inspeção: </strong>{inspection.nextInspectionDate || '—'}</span>
								</div>
							</div>
							<div className="painel">
								<Typography variant="subtitle1">Observações gerais</Typography>
								<p>{inspection.generalObservation || 'Sem observações registradas.'}</p>
							</div>
							<div className="painel">
								<Typography variant="subtitle1">Resumo da inspeção</Typography>
								<EpiDonut
									total={dossier.okItems + dossier.nonConformItems + dossier.notApplicableItems}
									totalLabel="Total"
									segments={[
										{ label: 'Conforme', value: dossier.okItems, color: EPI_COLORS.success },
										{ label: 'Não conforme', value: dossier.nonConformItems, color: EPI_COLORS.danger },
										{ label: 'Não aplicável', value: dossier.notApplicableItems, color: EPI_COLORS.neutral }
									]}
								/>
							</div>
							<div className="painel">
								<Typography variant="subtitle1">Ciências registradas</Typography>
								<div className="ciencias">
									<div>
										<span>Ciência do trabalhador</span>
										<EpiStatusChip
											label={dossier.acknowledgedAt ? 'Registrada' : 'Não registrada'}
											color={dossier.acknowledgedAt ? EPI_COLORS.success : EPI_COLORS.neutral}
											filled
										/>
										<span className="carimbo">{dossier.acknowledgedAt || '—'}</span>
									</div>
									<div>
										<span>Ciência de pendências de exames</span>
										<EpiStatusChip
											label={dossier.examAcknowledgedAt ? 'Registrada' : 'Sem pendência'}
											color={dossier.examAcknowledgedAt ? EPI_COLORS.success : EPI_COLORS.neutral}
											filled
										/>
										<span className="carimbo">{dossier.examAcknowledgedAt || '—'}</span>
									</div>
								</div>
							</div>
						</div>
					)}
					{tab === 1 && (
						<div className="painel tabela-itens">
							<div className="header-tabela">
								<p>EPI</p>
								<p>CA</p>
								<p>Última entrega</p>
								<p>Status</p>
								<p>Detalhamento</p>
							</div>
							{inspection.items.map((item, index) => {
								const detalhamento = item.observation
									|| (item.immediateAction && (IMMEDIATE_ACTIONS[item.immediateAction] || item.immediateAction))
									|| '—';
								return (
									<div className="linha" key={item.id || index}>
										<p title={item.epiName}>{item.epiName}</p>
										<p>{item.ca || '—'}</p>
										<p>{item.lastDeliveryDate || '—'}</p>
										<p style={{ color: itemStatusColors[item.status || ''] }}>
											{itemStatusLabels[item.status || ''] || '—'}
										</p>
										<p title={detalhamento}>
											{detalhamento}
										</p>
									</div>
								);
							})}
						</div>
					)}
					{tab === 2 && (
						<div className="painel">
							{!dossier.correctiveActions.length
								? <p>Nenhuma não conformidade registrada nesta inspeção.</p>
								: dossier.correctiveActions.map(action => (
									<div className="nao-conformidade" key={action.id}>
										<Typography variant="subtitle1">{action.epiName}</Typography>
										<span><strong>Motivo: </strong>
											{reasonLabel(action.nonConformityReason, action.nonConformityReasonDetail)}</span>
										<span><strong>Problema: </strong>{action.problemDescription || '—'}</span>
										<span><strong>Ação imediata: </strong>
											{IMMEDIATE_ACTIONS[action.immediateAction || ''] || action.immediateAction || '—'}</span>
									</div>
								))
							}
						</div>
					)}
					{tab === 3 && (
						<div className="painel">
							{!dossier.correctiveActions.length
								? <p>Nenhuma ação corretiva aberta por esta inspeção.</p>
								: dossier.correctiveActions.map(action => (
									<div className="acao-corretiva" key={action.id}>
										<Typography variant="subtitle1">{action.epiName}</Typography>
										<EpiStatusChip
											label={action.overdue
												? 'Vencida'
												: actionStatusLabels[action.status] || action.status}
											color={action.overdue
												? EPI_COLORS.danger
												: actionStatusColors[action.status] || EPI_COLORS.neutral}
										/>
										<span><strong>Responsável: </strong>{action.responsibleName || '—'}</span>
										<span><strong>Prazo: </strong>{action.dueDate || '—'}</span>
										<span><strong>Ação corretiva: </strong>
											{action.correctiveAction || 'Ainda não definida.'}</span>
									</div>
								))
							}
						</div>
					)}
					{tab === 4 && (
						<div className="painel evidencias">
							{!dossier.evidenceFiles.length && !dossier.signatures.length
								? <p>Nenhum arquivo registrado nesta inspeção.</p>
								: <Fragment>
									{dossier.evidenceFiles.map(name => (
										<figure key={name}>
											<img src={files[name]} alt={`Evidência ${name}`} />
											<figcaption>Evidência</figcaption>
										</figure>
									))}
									{dossier.signatures.map(signature => (
										<figure key={signature.fileName}>
											<img src={files[signature.fileName]} alt={`Assinatura ${signature.role}`} />
											<figcaption>
												{signature.role === 'TRABALHADOR'
													? 'Assinatura do trabalhador'
													: 'Assinatura do responsável'}
												{' · '}{signature.signedAt}
											</figcaption>
										</figure>
									))}
								</Fragment>
							}
						</div>
					)}
					{tab === 5 && (
						<div className="painel linha-do-tempo">
							{dossier.timeline.map((entry, index) => (
								<div className="entrada" key={index}>
									<span className="marca" />
									<div>
										<strong>{entry.label}</strong>
										<span>{entry.occurredAt}{entry.author ? ` · ${entry.author}` : ''}</span>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</Fragment>
	);
};

export default EpiInspectionDossier;
